import brandVisibilityKeyframePrompt from "bundle-text:../../../../../../prompts/Brand-Visibility-keyframe.prompt.md";

import {
  abstractTopicPrompt,
  backgroundVariationPrompt,
  campaignTopicPrompt,
  formatter,
  keyframePrompt,
  keyVisualsSection,
  objectVariationPrompt,
  referenceKeyElements,
  safeKeyframePrompt,
  systemUser,
} from "./variationPrompts";

/** No words, signs, logos or people — the abstract key visuals' constant. */
const NO_TEXT_NEGATIVE_PROMPT = "words, signs, logos, icons, people, faces";

const COMBINE_PROMPT = `Combine two references into one seamless advertising photograph. Reference 1 is the subject, isolated on a temporary studio gradient backdrop, shown at a specific angle and rotation — discard that backdrop, preserving the subject's exact shape, proportions, colors, materials, branding, and design with zero redesign, and its given rotation exactly as shown.

Reference 2 is a style reference only, not a background to copy — take from it only the general mood, environment type, color palette, and light quality, not its literal composition, camera angle, or content. If it contains a real person, use their presence only as scene-type context — do not regenerate that specific person.

Design a new environment genuinely perspective-consistent with the subject's own angle in Reference 1 — ground plane, horizon, and structure must match its orientation, never looking inserted sideways. Make this environment a distinct, fresh take on Reference 2's mood, since this subject may be composited multiple times — each result must read as a genuinely different environment, not a near-duplicate of Reference 2 or other variants.

Composition safety margin: regardless of how tightly Reference 1 is cropped, compose the final image with generous negative space around the subject — no more than roughly 40-50% of the frame's shorter dimension (less for tall/narrow subjects, clear margin above/below). Keep clear of a margin zone on at least one side for a possible text/CTA overlay. Never crop a person's head or face.

Add a realistic contact shadow and reflections matching the environment's light. Blend edges seamlessly, no halo. Keep the mood positive. Do not add extra objects, text, logos, or watermarks. Style: professional advertising photography, realistic grounding.`;

/**
 * Every image variation type, in one place.
 *
 * A template's `imageVariations[].type` selects an entry here, and that entry
 * is the *whole* definition of the type: how its prompt is written, and how the
 * request that generates it is built. Adding a type is adding an entry —
 * nothing else dispatches on `type`.
 *
 * @typedef {object} VariationType
 * @property {(data: {prompt?: string|Array<string>, campaign?: object, customer?: object,
 *   visual?: object, visuals?: Array<object>}) => (string|object|null)}
 *   [prompt] - Builds the variation's prompt: a Zoltar ID/user-message
 *   request to generate one from, a plain string that already is the prompt,
 *   or `null`. A type without it is never prompted — and, since generation
 *   waits for a `visualPrompt`, only generates once something else writes one.
 * @property {string} [model] - The model to generate with. Wins over a `model`
 *   set on the template variation.
 * @property {string} [defaultModel] - The model to generate with unless the
 *   template variation picks its own.
 * @property {"upload"|"none"|"file"} [reference] - How the reference image
 *   reaches the model: as a Firefly upload (`"upload"`, the default), not at
 *   all (`"none"`, generated from the prompt alone), or as a source file
 *   (`"file"`, for actions that edit the image itself).
 * @property {string} [negativePrompt] - Sent with the request, over anything
 *   the template variation set.
 * @property {string} [action] - A non-generative action (Photoshop) rather
 *   than a model: it edits the reference image and takes no prompt.
 * @property {boolean} [videoSettings] - Forward the variation's
 *   `videoSettings` to the model.
 *
 * @type {Record<string, VariationType>}
 */
export const VARIATION_TYPES = {
  // ---------------------------------------------------------------------------
  // Campaign key visuals (CSC / UCX)
  // ---------------------------------------------------------------------------

  "CSC-audience": {
    prompt: ({ customer, campaign, visual, visuals }) =>
      systemUser(
        "CSC-image-audience",
        `I have a client named: ${customer?.name}

I want to focus on the campaign about:
[Campaign]: ${campaign?.title}
[Audience]: ${campaign?.audience}${
          !!keyVisualsSection({ visual, visuals })
            ? `\n\n${keyVisualsSection({ visual, visuals })}`
            : ""
        }${
          referenceKeyElements({ visual, visuals }).length
            ? `\n\nKey elements:
${formatter.format(referenceKeyElements({ visual, visuals }))}`
            : ""
        }`,
      ),
    model: "image5",
    reference: "upload",
  },

  audience: {
    // Legacy alias of `CSC-audience` — kept generating, never prompted.
    model: "image5",
    reference: "upload",
  },

  "CSC-abstract": {
    prompt: (data) => abstractTopicPrompt(data, data.campaign?.title),
    model: "image4_ultra",
    reference: "upload",
    negativePrompt: NO_TEXT_NEGATIVE_PROMPT,
  },

  abstract: {
    // Legacy alias of `CSC-abstract` — kept generating, never prompted.
    model: "image4_ultra",
    reference: "upload",
    negativePrompt: NO_TEXT_NEGATIVE_PROMPT,
  },

  "UCX-topic-abstract": {
    prompt: (data) =>
      abstractTopicPrompt(data, data.campaign?.["Campaign-topic"]),
    model: "image4_ultra",
    reference: "upload",
    negativePrompt: NO_TEXT_NEGATIVE_PROMPT,
  },

  "UCX-campaign-audience": {
    prompt: ({ customer, campaign, visual, visuals }) =>
      systemUser(
        "common-image-audience",
        `[Context]:
[Customer]: ${customer?.name}
[Audience]: ${campaign?.["Audience"] ?? campaign?.["Campaign-audience-name"]}
[City]: ${campaign?.["City"] ?? campaign?.["Persona-1-location"]}

${keyVisualsSection({ visual, visuals })}`,
      ),
    model: "image5",
    reference: "upload",
  },

  "UCX-audience-product": {
    // Legacy alias of `UCX-campaign-audience` — kept generating, never prompted.
    model: "image5",
    reference: "upload",
  },

  "UCX-location": {
    prompt: ({ campaign }) =>
      systemUser(
        "UCX-image-location-based",
        `[Location-visited]: ${campaign?.["Location-visited"]}${
          !!campaign?.["Location-based-text"]
            ? `\n[Message]: ${campaign?.["Location-based-text"]}`
            : ""
        }`,
      ),
    model: "image5",
    reference: "none",
  },

  "UCX-tv": {
    prompt: ({ campaign }) =>
      systemUser(
        "UCX-image-connected-tv",
        `[Audience]: ${campaign?.["Persona-1-audience"]}
[Connected-TV-program-topic]: ${campaign?.["Connected-TV-program-topic"]}`,
      ),
    model: "image5",
    reference: "none",
  },

  "UCX-location-thumbnail": {
    prompt: ({ campaign }) =>
      systemUser(
        "UCX-image-location-based",
        `[Location-visited]: ${campaign?.["Persona-1-location"]}

This image will be used as a thumbnail, so it should be more focused on the location and less on the product. The image should look good when cropped to a square format and when displayed at smaller sizes (e.g. 200x200).`,
      ),
    model: "image5",
    reference: "none",
  },

  "UCX-audience-thumbnail": {
    prompt: ({ campaign }) =>
      systemUser(
        "UCX-image-audience-thumbnail",
        `[Audience]: ${campaign?.["Audience"] ?? campaign?.["Campaign-audience-name"]}`,
      ),
    model: "image5",
    reference: "none",
  },

  "UCX-campaign-topic": {
    prompt: (data) =>
      campaignTopicPrompt(
        data,
        "This image will be used as a hero image for the sign-up page of a campaign website, so it should be visually striking and clearly convey the campaign topic. The image should look good when displayed in a wide format (e.g. 1200x600).",
      ),
    model: "image4_ultra",
    reference: "upload",
    negativePrompt: NO_TEXT_NEGATIVE_PROMPT,
  },

  "UCX-share": {
    prompt: (data) =>
      campaignTopicPrompt(
        data,
        "This image will be used as a preview image when sharing campaign content on social media, so it should be eye-catching and clearly represent the campaign. The image should look good when displayed in a square format (e.g. 1200x1200).",
      ),
    model: "image4_ultra",
    reference: "upload",
    negativePrompt: NO_TEXT_NEGATIVE_PROMPT,
  },

  // ---------------------------------------------------------------------------
  // Keyframes and camera variations
  // ---------------------------------------------------------------------------

  Keyframe: {
    prompt: () =>
      keyframePrompt(
        "remove all people and non-product text (e.g., slogans, headlines, overlays),",
      ),
    defaultModel: "nano-banana-3",
    reference: "upload",
  },

  "Keyframe-people": {
    prompt: ({ campaign }) =>
      keyframePrompt(
        "remove all non-product text (e.g., slogans, headlines, overlays),",
        campaign?.["Campaign-audience-name"]
          ? `\n\nThe image should appeal to the following audience: ${campaign?.["Campaign-audience-name"]}`
          : "",
      ),
    defaultModel: "nano-banana-3",
    reference: "upload",
  },

  "Keyframe-people-safe": {
    prompt: () => safeKeyframePrompt(),
    defaultModel: "nano-banana-3",
    reference: "upload",
  },

  "Keyframe-brand-visibility": {
    // Filled here: generic interpolation drops lines with unresolved placeholders.
    prompt: ({ campaign }) => {
      const inputs = {
        "company-name": campaign?.["Company-name"] ?? "",
        product: campaign?.["Campaign-product"] ?? "",
      };

      return brandVisibilityKeyframePrompt.replace(
        /\{\{(company-name|product)\}\}/g,
        (_, key) => inputs[key],
      );
    },
    defaultModel: "nano-banana-3",
    reference: "upload",
  },

  "Keyframe-extend": {
    prompt: () =>
      `preserve original composition and subject exactly, extend background naturally and seamlessly, no new foreground elements, consistent lighting and style`,
    model: "image5",
    reference: "upload",
  },

  Angle: {
    prompt: () =>
      `Use the reference image as the source of truth. Keep every subject IDENTICAL (shape/geometry, proportions, materials, colors, textures, small details, and any original label text/branding on the subject). Do not redesign or reinterpret anything. Keep the same lighting style and background style.

Create a clearly different result by changing ONLY the camera angle and framing/composition (crop, distance, perspective, depth of field).
IF there is ONE main object: make the change obvious by using an extreme viewpoint (top-down, bottom-up, strong diagonal), a tighter or wider crop, and a stronger placement (off-centre, more negative space). The object may be repositioned or rotated in space only if it remains physically identical.
IF there are MULTIPLE objects: keep all objects identical and present, but improve composition by rearranging their spacing and relative placement (closer/farther, slight overlap, clearer hierarchy). Do not add or remove objects.
IF there is a PERSON: do not change the person’s identity, face, hair, clothing, body shape, pose, or expression. Only change camera angle and crop to improve composition.
Photorealistic result. No new elements. No stylization.`,
    defaultModel: "nano-banana-3",
    reference: "upload",
  },

  Zoom: {
    prompt: () =>
      `Create an abstract variation by zooming into the object.
Focus on material texture, surface detail, and form.
Extreme close‑up composition.
Keep original lighting, color, and material characteristics.
The object must remain recognizable through texture and form, not shape alone.
No new colors, no stylistic effects.`,
    defaultModel: "nano-banana-3",
    reference: "upload",
  },

  Mix: {
    prompt: () =>
      `Create another variation of this image, keeping the same object type and style but changing the zoom, crop or mix.`,
    defaultModel: "nano-banana-3",
    reference: "upload",
  },

  Composition: {
    prompt: () =>
      `Create another variation of this image, keeping the same object type and style but changing the composition.`,
    defaultModel: "nano-banana-3",
    reference: "upload",
  },

  Sketch: {
    prompt: () =>
      `Keep the exact same camera angle, framing, proportions, and composition.
Transform the image into a black and white sketch.
Line-based drawing style, hand-drawn or pencil-like.
No color, no shading tones other than black and white.
Do not simplify or exaggerate shapes.
The sketch must align perfectly with the original structure.
White, paper-like background.`,
    model: "image5",
    reference: "upload",
  },

  Draft: {
    prompt: () =>
      `Using only the photo as reference, generate a multi‑panel step‑by‑step digital sketch sequence of the object, arranged in a clean grid on a pure white background.
Early panels:
Show very loose geometric construction using simple boxes and cylinders with perspective guide lines. Lines are light, rough, and overlapping.
Middle panels:
Gradually add loose contour lines and basic structural details. Construction lines remain visible. The drawing stays sketchy and exploratory.
Final two panels:
Introduce limited color accents sampled from the original photo. Color should be subtle and incomplete, applied loosely like a light digital watercolor or color pencil wash. Lines remain dominant. No full fill, no realism, no polished rendering.
Style: digital sketch, industrial design drawing, thin grey or black lines, imperfect strokes, visible process.
Background: pure white only.
Constraints: no paper texture, no canvas grain, no scanning artefacts, no shading, no gradients, no photorealism.
No text of any kind: no numbers, no captions, no labels, no watermarks, no logos.`,
    defaultModel: "nano-banana-3",
    reference: "upload",
  },

  // ---------------------------------------------------------------------------
  // Product and 3D renders
  // ---------------------------------------------------------------------------

  "3D": {
    prompt: () =>
      `Recreate the object as a precise 3D model.
Render the object in white material on a pure white background.
No textures, no color, no branding.
Change the camera angle and/or composition compared to the reference image, while keeping correct proportions and scale.
Soft, neutral studio lighting.
Clean, professional 3D render as if from a 3D modelling or CAD visualization program.
No artistic or stylized effects.`,
    defaultModel: "nano-banana-3",
    reference: "upload",
  },

  "3D-simple": {
    prompt:
      () => `Create an image of this product, visualized as a simple 3D render.

Keep its exact shape, proportions, and details. Use a uniform light-grey matte material with no branding, colors, or textures.

Grey #707070 background, perspective grid lines on the floor, soft lighting, 3/4 view.`,
    defaultModel: "nano-banana-3",
    reference: "upload",
  },

  "3D-mesh": {
    prompt:
      () => `Create an image of the exact product from the reference, visualized as a 3D mesh render in the same view.

Keep its exact shape, proportions, and details. Use a uniform white matte material with no branding, colors, or textures, and with clearly visible blue mesh/wireframe lines following the product geometry.

Black background, soft lighting, 3/4 view.`,
    defaultModel: "nano-banana-3",
    reference: "upload",
  },

  Object: {
    prompt: () =>
      `This is the source "keyframe" in a marketing pipeline. It will split into an isolated subject and a standalone background, later recombined with other new backgrounds/subjects. Produce the isolated subject asset here.

Identify the true subject using this priority, not just raw depth in the frame:
1. If any manufactured object, vehicle, structure, or venue carries visible branding, a logo, or brand name (printed on its body, livery, or packaging), that branded item is the subject — even if people or other elements are closer to camera.
2. Otherwise, if a person is clearly wearing, holding, or using a product as the evident focus (fashion, apparel, a held package), that is the subject.
3. Otherwise, fall back to the foreground hero element: closest to camera, sharpest focus, largest scale, at the frame's main focal point.

Anything not selected counts as removable environment, including people or props staged nearby without being the branded item itself.

Keep the subject completely untouched in its original position, scale, crop, angle, and pose, so it aligns pixel-for-pixel with the original composition for later recombination with the background.

Remove everything else and replace it with a soft, seamless studio gradient: a gentle, low-saturation tint pulled from the subject's own dominant colors, brighter near the subject, deeper at the edges. Avoid flat white or clashing color. Add a soft, physically plausible contact shadow matching one soft overhead-front light source.

Do not redesign, recolor, or reinterpret the subject, and do not add new objects, text, logos, or watermarks.`,
    defaultModel: "nano-banana-3",
    reference: "upload",
  },

  "Object-variation": {
    prompt: () =>
      objectVariationPrompt(
        `a complementary three-quarter angle on the opposite side from a typical default view`,
      ),
    defaultModel: "nano-banana-3",
    reference: "upload",
  },

  "Object-variation-side": {
    prompt: () => objectVariationPrompt(`a direct side profile view`),
    defaultModel: "nano-banana-3",
    reference: "upload",
  },

  "Object-variation-front": {
    prompt: () =>
      objectVariationPrompt(
        `a front-facing (or back-facing, whichever is more visually informative) view`,
      ),
    defaultModel: "nano-banana-3",
    reference: "upload",
  },

  "Object-3D": {
    prompt: () =>
      `Convert the reference image into a clean 3D digital-twin mesh preview render of the main object shown, whatever it is.
Preserve the exact silhouette, proportions, and geometric structure of the reference object with no reinterpretation of shape or design.
Remove all color, material texture, branding, logos, and surface graphics — replace with a uniform matte light-grey clay material (studio CAD-preview look).
Overlay a visible, even quadrilateral wireframe grid across the entire surface of the object, following its true topology and curvature.
Fully isolate the object from its background; float it in empty space at a slight three-quarter angle, matching the object's natural resting orientation.
Add soft, neutral studio lighting from above-front with gentle ambient occlusion in the recesses, and a soft diffused contact shadow beneath the object.
Background: seamless neutral grey-to-dark-grey radial gradient, no props, no text, no reflections, no background objects.
Overall style: clean CAD/3D-scan visualization render, high detail, sharp wireframe lines, no motion blur, no depth of field, centered composition.`,
    model: "image5",
    reference: "upload",
  },

  "Object-3D-macro": {
    prompt: () =>
      `Ignore the reference image's overall composition and camera angle entirely. Create an extreme close-up macro shot of a single small fragment of the object's surface, such as a corner, edge, seam, or panel intersection, filling most of the frame, with parts of it extending beyond the frame edges. Choose a fragment and camera angle clearly different from what a straightforward crop of the reference would show, as if the camera moved to a new position around the object before zooming in. Critically, do not distort, bend, round, warp, or invent any geometry: all edges, corners, and flat panels must stay exactly as straight, flat, or angled as in the reference object, with correct real-world perspective. Keep the same clay material and wireframe topology style, applied consistently to the visible fragment. Remove any residual printed text, labels, logos, or graphic artifacts, leaving pure clay and wireframe only. Soft neutral studio lighting, gentle ambient occlusion emphasizing surface detail and the wireframe grid. Background: seamless neutral grey-to-dark-grey gradient, softly out of focus behind the fragment, no props, no text. Style: clean CAD/3D-scan macro detail render, sharp wireframe lines, shallow depth of field isolating the close-up.`,
    model: "image5",
    reference: "upload",
  },

  Combine: {
    prompt: () => COMBINE_PROMPT,
    defaultModel: "nano-banana-3",
    reference: "upload",
  },

  // ---------------------------------------------------------------------------
  // Scenes, people and thumbnails
  // ---------------------------------------------------------------------------

  "TV-scene": {
    prompt: ({ campaign }) =>
      systemUser(
        "common-image-tv-commercial-scene",
        `[Campaign-product]: ${campaign?.["Campaign-product"] ?? ""}
[TV-commercial-scene]:
${campaign?.["TV-commercial-scene"]}`,
      ),
    defaultModel: "nano-banana-3",
    reference: "upload",
  },

  "User-thumbnail": {
    // Legacy alias of `TV-scene` — kept generating, never prompted.
    defaultModel: "nano-banana-3",
    reference: "upload",
  },

  Audience: {
    prompt: ({ campaign }) =>
      systemUser(
        "common-image-audience",
        `[Context]:
${campaign?.["Context"]}`,
      ),
    model: "image5",
    reference: "upload",
  },

  "OneAdobe-product-variation": {
    prompt:
      () => `Create a new product shot of the {{Product-name}} from a different angle.
Keep the product completely consistent with the provided reference image: same shape, proportions, colors, materials, details, branding, and design. Do not add, remove, or change any product features.
Place it on a clean grey #909090 background and use professional studio lighting.
No additional texts or icons.
Show the product from this angle: {{Angle}}`,
    defaultModel: "nano-banana-3",
    reference: "upload",
  },

  "OneAdobe-photoshoot": {
    prompt: () =>
      `Image 1 is the original key visual from a photoshoot. Image 2 is a reference showing {{KV-theme-short}}, which also appears in Image 1. Image 2 only identifies what must be excluded; do not use it as content, style, or inspiration for the output.

Create another photo from the same key visual photoshoot, at the same location and within the same overall scenario, but capture a distinctly different moment.

The image must clearly feel like it belongs to the same campaign and creative concept as the original key visual. Preserve the same visual energy, emotional tone, brand expression, styling, atmosphere, lighting mood, and overall creative intent. Keep the people's appearance, clothing, and styling consistent with Image 1.

Make the image clearly different from the original by changing multiple photographic attributes at once:
- camera angle
- camera height
- distance to subject
- lens choice and perspective
- framing and crop
- subject pose and body orientation
- direction of movement or gaze

Do not recreate the original composition. Avoid similar framing, similar crop, similar perspective, similar pose, or a near-duplicate viewpoint.

The following must not appear anywhere in the new image, including in the background, at the edges, or partially visible:
1. The element shown in Image 2 ({{KV-theme-short}}), in any form.
2. The product: {{Product-name}}, including its packaging, branding, or lookalikes.
Wherever these would naturally have appeared, show what would realistically be there instead, matching the surrounding textures, lighting, shadows, and perspective. No leftover traces, ghosting, or blur.

The result should feel like a different selected hero shot from the same premium key visual photoshoot, not a variation of the same frame and not a generic lifestyle photo. Keep the same level of visual impact, campaign quality, and key visual presence as the original. Keep the main subject as the visual focal point and positioned near the center area.

Photorealistic. No packshot, no visual motif, no additional CGI elements, no text, image only.`,
    defaultModel: "nano-banana-3",
    reference: "upload",
  },

  "OneAdobe-location": {
    prompt:
      () => `Image 1 is the original key visual from a photoshoot. Image 2 is a reference showing {{KV-theme-short}}, which also appears in Image 1. Image 2 only identifies what must be excluded; do not use it as content, style, or inspiration for the output.

Create another photo from the same photoshoot, at the same location, showing only the location itself: an empty, clean environment with no people, no subjects, and no props.

The image must clearly feel like it belongs to the same campaign. Preserve the same atmosphere, lighting mood, time of day, color grading, and photographic style, as well as the location's architecture, materials, textures, and colors.

Make the image clearly different from the original by changing multiple photographic attributes at once:
- camera angle
- camera height
- distance
- lens choice and perspective
- framing and crop

Do not recreate the original composition. Avoid similar framing, similar crop, similar perspective, or a near-duplicate viewpoint. Show a different part of the space or a different perspective on it.

Remove everything except the location. The following must not appear anywhere in the new image, including in the background, at the edges, or partially visible:
1. The element shown in Image 2 ({{KV-theme-short}}), in any form.
2. The product: {{Product-name}}, including its packaging, branding, or lookalikes.
3. People: no people, figures, silhouettes, reflections of people, shadows of people, or body parts.
4. Any other foreground subjects, props, signage, banners, or decorative objects that were added for the photoshoot. Keep only the permanent, built or natural elements of the location itself (structure, surfaces, furniture or fixtures that belong to the space, vegetation, sky).
Wherever removed elements would naturally have appeared, show what would realistically be there instead, matching the surrounding textures, lighting, shadows, and perspective. No leftover traces, ghosting, or blur.

The result should look like a professional, premium location photograph: a clean, empty, inviting space with a strong sense of place. No single subject is required as the focal point; compose the frame around the architecture and atmosphere of the location.

Photorealistic. No packshot, no visual motif, no additional CGI elements, no text, image only.`,
    defaultModel: "nano-banana-3",
    reference: "upload",
  },

  "OneAdobe-KV-variation": {
    prompt:
      () => `Create another photo from the same key visual photoshoot for {{Product-name}} campaign, at the same location and within the same overall scenario, but capture a distinctly different moment.

The image must clearly feel like it belongs to the same campaign and creative concept as the original key visual. Preserve the same visual energy, emotional tone, brand expression, styling, atmosphere, lighting mood, and overall creative intent. Keep the people's appearance, clothing, and styling consistent with Image 1.

Make the image clearly different from the original by changing multiple photographic attributes at once:
- camera angle
- camera height
- distance to subject
- lens choice and perspective
- framing and crop
- subject pose and body orientation
- direction of movement or gaze

Do not recreate the original composition. Avoid similar framing, similar crop, similar perspective, similar pose, or a near-duplicate viewpoint.

The result should feel like a different selected hero shot from the same premium key visual photoshoot, not a variation of the same frame and not a generic lifestyle photo. Keep the same level of visual impact, campaign quality, and key visual presence as the original. Keep the main subject as the visual focal point and positioned near the center area.

Photorealistic, no text, image only.`,
    defaultModel: "nano-banana-3",
    reference: "upload",
  },

  "OneAdobe-user-grid": {
    prompt:
      () => `NO TEXT. NO TYPOGRAPHY. NO LETTERS. NO WORDS. NO NUMBERS. TEXT-FREE IMAGE ONLY.

4x4 image grid of user thumbnails — 16 square user photos, filling the whole area, no margins, no borders, no text, just images. Each face covers at least 50% of its square.

All 16 must be visibly different, unique people — never repeat the same person or look. Make them a realistic, diverse cross-section of the target audience below, varying naturally in age, gender, ethnicity, style and setting in the proportions you'd actually see in that group.

Each photo must have a clearly different background — vary the setting, location and lighting so no two share the same environment. Draw the settings from places this audience would realistically be.

Target audience is: {{Product-target-audience-demographics}}`,
    defaultModel: "nano-banana-3",
    reference: "upload",
  },

  // Alternative, prompt-driven orbit shot kept for reference:
  //   systemUser("common-video-time-freeze-orbit", data?.visual?.labeling)
  Video: {
    prompt: () =>
      `Camera is almost static, like a fixed tripod with a tiny rotation.
No zoom, pan, tilt, or shake.`,
    model: "video",
    reference: "upload",
    videoSettings: true,
  },

  // ---------------------------------------------------------------------------
  // Backgrounds
  // ---------------------------------------------------------------------------

  Background: {
    prompt: () =>
      `Identify the true subject of this image using this priority, not just raw depth in the frame:
1. If any manufactured object, vehicle, structure, or venue carries visible branding, a logo, or brand name (printed on its body, livery, or packaging), that branded item is the subject — even if people or other elements are closer to camera.
2. Otherwise, if a person is clearly wearing, holding, or using a product as the evident focus (fashion, apparel, a held package), that is the subject.
3. Otherwise, fall back to the foreground hero element: closest to camera, sharpest focus, largest scale, at the frame's main focal point.

Remove that identified subject from this image completely, including any shadows or reflections it casts. Reconstruct and fill in the area where it was removed so the background looks natural, seamless, and continuous — as if it was never there. Preserve the original background's colors, lighting, textures, perspective, and depth of field exactly as they are. Do not add any new objects, patterns, or elements that weren't part of the original background. The output should be a clean, empty version of the same scene.`,
    defaultModel: "nano-banana-3",
    reference: "upload",
  },

  "Background-variation-subtle": {
    prompt: () =>
      backgroundVariationPrompt(
        `For this variant, make the change subtle and closely related to the reference.`,
      ),
    model: "image5",
    reference: "upload",
  },

  "Background-variation-distinct": {
    prompt: () =>
      backgroundVariationPrompt(
        `For this variant, make the change clearly distinct from the reference — a different setting, palette, or material within the same general category.`,
      ),
    model: "image5",
    reference: "upload",
  },

  "Background-variation-dramatic": {
    prompt: () =>
      backgroundVariationPrompt(
        `For this variant, make the change as dramatic and far-reaching as plausible, while staying physically and stylistically sensible for this subject.`,
      ),
    model: "image5",
    reference: "upload",
  },

  // ---------------------------------------------------------------------------
  // Config-driven and non-generative types
  // ---------------------------------------------------------------------------

  // Generic, fully config-driven type: the variation supplies its own prompt
  // body, which — like every other entry here — is then expanded against
  // `campaign` + `variation.values`. Covers the static, campaign-backed and
  // mixed cases in one type.
  prompt: {
    prompt: ({ prompt }) => prompt ?? null,
    defaultModel: "nano-banana-3",
    reference: "upload",
  },

  "labeling-prompt": {
    prompt: ({ prompt, visual }) => {
      const fields = Array.isArray(prompt) ? prompt : [prompt];
      const parts = fields.map((field) => visual?.labeling?.[field]);

      return parts.length > 0 &&
        parts.every(
          (part) => typeof part === "string" && part.trim().length > 0,
        )
        ? parts.join("\n\n")
        : null;
    },
    defaultModel: "nano-banana-3",
    reference: "none",
  },

  // Non-generative Photoshop action. It needs no prompt, but the pipeline only
  // generates a variation once it has a `visualPrompt`, so this doubles as the
  // marker that unblocks it and as the label shown in the edit dialog.
  "Action:RemoveBackground": {
    prompt: () => "Remove background",
    model: "photoshop",
    reference: "file",
    action: "removeBackground",
  },
};

/** The definition of a variation type, or `undefined` for an unknown one. */
export const getVariationType = (type) => VARIATION_TYPES[type];
